<template>
  <EditDialog
    v-model:visible="showModal"
    :title="$t('views.botSetting.BotForm.components.ModelSetting.273dbab6ca6e5ae7385cc63ff12a2a92')"
    :width="620"
    :mask-closable="false"
    is-show-cancel
    :padding="30"
    :hide-footer="disabled"
    @cancel="showModal = false"
    @submit="handleSubmit"
  >
    <div
      :class="{
        disabled
      }"
      class="dialog-body"
    >
      <DynamicsForm
        :model="formModel"
        :setting="baseSetting"
        :label-width="140"
        :wrapper-width="380"
        hide-required-mark
        :group-collapse="false"
        tips-placement="left"
        class="common-form"
        tips-icon="USolidHelpCircle"
      >
        <template #llm_model>
          <ModelSelect
            v-model="formModel.llm_model"
            :options="modelOption"
            :width="300"
            @change="chatModelChange"
          />
        </template>
        <template #fn_call>
          <div>
            {{
              formModel.support_function_call
                ? $t(
                    'views.botSetting.BotForm.components.ModelSetting.0fc75bc36324e675c303015c696c746d'
                  )
                : $t(
                    'views.botSetting.BotForm.components.ModelSetting.d7b4669a64da497b871a240d4dcc421a'
                  )
            }}
          </div>
        </template>
        <template #divergence>
          <div style="margin-top: -4px">
            <u-slider
              v-model="formModel.divergence"
              :min="0"
              :max="20"
              :step="2"
              :marks="{
                0: $t(
                  'views.botSetting.BotForm.components.ModelSetting.7a36282dfdc472aa7c4d5cf4cb6fdedd'
                ),
                20: $t(
                  'views.botSetting.BotForm.components.ModelSetting.1378e0ce50d9102957096b2eaec18ace'
                )
              }"
              :tip-formatter="value => `${value / 20}`"
            />
          </div>
        </template>
        <template #top_p>
          <div style="margin-top: -4px">
            <u-slider
              v-model="formModel.top_p"
              :min="1"
              :max="100"
              :step="1"
              :marks="{
                0: '0',
                100: '1'
              }"
              :tip-formatter="value => `${value / 100}`"
            />
          </div>
        </template>
        <template #max_tokens>
          <div style="margin-top: -4px">
            <u-slider
              v-model="formModel.max_tokens"
              :min="minToken"
              :max="maxToken"
              :step="1"
              :marks="{
                [minToken]: minToken,
                [maxToken]: maxToken
              }"
            />
          </div>
        </template>
      </DynamicsForm>
    </div>
  </EditDialog>
</template>
<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { DynamicsForm } from '@koi-design/vix-components';
import { getModelListApi } from '@/api/robot';
import { message } from '@koi-design/uxd-ui';
import { cloneDeep } from 'lodash-es';
import ModelSelect from '@/components/ModelSelect.vue';
import { applyAiWriteLlmModelResolve } from '@/libs/resolveModelReplacement';

export default defineComponent({
  name: 'AiWriteModelSetting',
  components: {
    DynamicsForm,
    ModelSelect
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submit'],
  setup(_, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const showModal = ref(false);
    const formModel = ref({});
    const modelOption = ref([]);
    const minToken = ref(100);
    const maxToken = ref(100);
    const homelandInfo = computed(() => store.getters.homelandInfo);
    const baseSetting = ref([
      {
        name: 'llm_model',
        label: t(
          'views.aiWrite.templateList.components.ModelSetting.a1d07297766d3a26bc031b11b89054e9'
        ),
        type: 'slot',
        slot: 'llm_model',
        props: {
          dropdownMenuClass: 'menu-list'
        }
      },
      {
        type: 'slot',
        slot: 'fn_call',
        label: t(
          'views.botSetting.BotForm.components.ModelSetting.880e021c47ca36588a2289e373054c43'
        ),
        name: 'support_function_call',
        tips: t('views.botSetting.BotForm.components.ModelSetting.74f3b5315f6fd71f3eb737afa916c23d')
      },
      {
        type: 'slot',
        label: t('views.botSetting.BotForm.components.BaseForm.bf0a1201b6543984cd2c0020093018fc'),
        slot: 'divergence',
        tips: t('views.botSetting.BotForm.components.BaseForm.0d5d0424052224e0f5006732403e7d66')
      },
      {
        type: 'slot',
        label: 'Top-P',
        slot: 'top_p',
        tips: t('views.botSetting.BotForm.components.ModelSetting.0bca4912a9cbc133158f621ef5152770')
      },
      {
        type: 'slot',
        label: t(
          'views.botSetting.BotForm.components.ModelSetting.2ca24c4d7f1c639361d608174e447a4b'
        ),
        slot: 'max_tokens'
      }
    ]);
    const modelList = computed(() => {
      return modelOption.value.flatMap(item => item.children || []);
    });
    const chatModelChange = value => {
      const obj = modelList.value.find(item => item.value === value);
      if (!obj) return;
      formModel.value.top_p = (obj.top_p ?? 1) * 100;
      formModel.value.llm_model_name = obj.label;
      minToken.value = obj.min_tokens;
      maxToken.value = obj.max_tokens;
      formModel.value.max_tokens = obj.default_tokens;
      formModel.value.support_function_call = obj.support_function_call;
    };
    const handleSubmit = async () => {
      formModel.value.top_p = (formModel.value.top_p ?? 100) / 100;
      emit('submit', formModel.value);
      showModal.value = false;
    };
    const show = async data => {
      showModal.value = true;
      formModel.value = cloneDeep(data);
      const res = await getModelListApi({
        homeland_id: homelandInfo.value.homeland,
        option_type: 'ai_write'
      }).catch(error => {
        message.error(error);
      });
      modelOption.value = res || [];
      await applyAiWriteLlmModelResolve(formModel.value, modelOption);
      formModel.value.top_p = (formModel.value.top_p ?? 1) * 100;
      const target = modelList.value.find(item => item.value === formModel.value.llm_model);
      if (target) {
        minToken.value = target.min_tokens;
        maxToken.value = target.max_tokens;
      }
    };
    const sliderRef = ref(null);
    const divergenceRef = ref(null);
    const maxTokensRef = ref(null);
    return {
      show,
      formModel,
      showModal,
      handleSubmit,
      baseSetting,
      sliderRef,
      divergenceRef,
      maxTokensRef,
      minToken,
      maxToken,
      modelOption,
      chatModelChange
    };
  }
});
</script>
<style lang="less" scoped>
.dialog-body {
  &.disabled {
    .uui-slider {
      pointer-events: none;
    }

    .uui-radio-group {
      pointer-events: none;
    }

    :deep(.uui-radio-group) {
      pointer-events: none;
    }

    :deep(.uui-select) {
      pointer-events: none;
    }

    .context-num {
      pointer-events: none;
    }
  }
}
</style>
